import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import { authenticatedFetch } from "../../utils/api";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import ProgressBar from "../ProgressBar";
import { formatInteger, truncateText, PROGRAM_NAME_MAX_WIDTH } from "../../utils/formatters";
import ShareProgramDialog from "../ShareProgramDialog";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Employee {
  id: number;
  name: string;
  phone: string;
}

interface Company {
  id: number;
  name: string;
}

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  design?: string;
  status?: string;
  completed_weight?: number;
}

interface Program {
  id: number;
  company: Company;
  lot?: string;
  status: string;
  program_details: ProgramDetail[];
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

const employeeShiftDetailSchema = z
  .object({
    record_id: z.number().optional(),
    company_id: z.union([z.number(), z.undefined()]).optional(),
    program_id: z.union([z.number(), z.undefined()]).optional(),
    dia: z.string().optional(),
    gauge: z.string().optional(),
    rolls: z.union([z.number(), z.string(), z.undefined()]).optional(),
    weight: z.union([z.number(), z.string(), z.undefined()]).optional(),
    machine_type: z.union([z.enum(["rib", "fine"]), z.undefined()]).optional(),
    _destroy: z.boolean().optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    if (data._destroy === true) return;

    if (data.company_id === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Company is required", path: ["company_id"] });
    }
    if (data.program_id === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Program is required", path: ["program_id"] });
    }
    if (!data.dia || data.dia === "") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Dia is required", path: ["dia"] });
    }
    if (!data.gauge || data.gauge === "") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Gauge is required", path: ["gauge"] });
    }
    
    const rolls = typeof data.rolls === "string" ? parseFloat(data.rolls) : data.rolls;
    if (rolls === undefined || rolls === null || rolls <= 0 || !Number.isInteger(rolls)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Rolls must be a positive integer", path: ["rolls"] });
    }
    
    const weight = typeof data.weight === "string" ? parseFloat(data.weight) : data.weight;
    if (weight === undefined || weight === null || weight <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Weight must be positive", path: ["weight"] });
    }
    
    if (!data.machine_type) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Machine type is required", path: ["machine_type"] });
    }
  });

const employeeShiftSchema = z.object({
  employee_id: z.number({ required_error: "Employee is required" }),
  shift_date: z.string().min(1, "Shift date is required"),
  shift_time: z.enum(["day", "night"], { required_error: "Shift time is required" }),
  employee_shift_details_attributes: z
    .array(employeeShiftDetailSchema)
    .refine(
      (details) => details.filter(d => !d._destroy).length >= 1,
      { message: "At least one detail is required" }
    ),
});

type EmployeeShiftFormData = z.infer<typeof employeeShiftSchema>;

interface NewlyStartedProgram {
  id: number;
  share_token: string;
  lot?: string;
  company_name: string;
  total_weight: number;
}

export default function EmployeeShiftDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [shift, setShift] = useState<any>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  
  // Share dialog state for newly started programs
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [newlyStartedPrograms, setNewlyStartedPrograms] = useState<NewlyStartedProgram[]>([]);
  const [currentShareIndex, setCurrentShareIndex] = useState(0);
  
  // Track deleted record IDs to send to backend for soft deletion
  const deletedDetailIdsRef = useRef<number[]>([]);

  const {
    control,
    formState: { errors },
    reset,
    watch,
    clearErrors,
    getValues,
  } = useForm<EmployeeShiftFormData>({
    resolver: zodResolver(employeeShiftSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      employee_id: 0,
      shift_date: "",
      shift_time: "day",
      employee_shift_details_attributes: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "employee_shift_details_attributes",
  });

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  // Watch for company/program changes and validate combinations
  useEffect(() => {
    if (!isEditing) return;

    fields.forEach((field, index) => {
      const companyId = watch(`employee_shift_details_attributes.${index}.company_id`);
      const programId = watch(`employee_shift_details_attributes.${index}.program_id`);
      const dia = watch(`employee_shift_details_attributes.${index}.dia`);
      const gauge = watch(`employee_shift_details_attributes.${index}.gauge`);
      
      if (companyId) {
        const filteredPrograms = getFilteredPrograms(companyId);
        
        if (filteredPrograms.length === 1 && programId !== filteredPrograms[0].id) {
          update(index, { ...field, company_id: companyId, program_id: filteredPrograms[0].id });
        }
        
        if (programId && !filteredPrograms.find(p => p.id === programId)) {
          update(index, { ...field, company_id: companyId, program_id: undefined, dia: "", gauge: "" });
        }
      } else if (programId) {
        update(index, { ...field, company_id: undefined, program_id: undefined, dia: "", gauge: "" });
      }
      
      if (programId && dia && gauge) {
        const program = programs.find((p) => p.id === programId);
        if (program?.program_details) {
          const validCombination = program.program_details.some(
            (d) => d.dia.toString() === dia && d.gauge === gauge
          );
          
          if (!validCombination) {
            const availableGauges = getAvailableGauges(programId, dia);
            const availableDias = getAvailableDias(programId, gauge);
            
            if (gauge && !availableGauges.includes(gauge)) {
              update(index, { ...field, gauge: "" });
            }
            if (dia && !availableDias.map(d => d.toString()).includes(dia)) {
              update(index, { ...field, dia: "" });
            }
          }
        }
      }
    });
  }, [watch, fields, isEditing, programs]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchShift(), fetchEmployees(), fetchCompanies(), fetchPrograms(), fetchMachines()]);
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setError("Failed to load shift details");
    } finally {
      setLoading(false);
    }
  };

  const fetchShift = async () => {
    const response = await authenticatedFetch(`${API_URL}/employee_shifts/${id}`);
    if (!response.ok) throw new Error("Failed to fetch employee shift");

    const data = await response.json();
    setShift(data);
    
    const transformedDetails = data.employee_shift_details.map((detail: any) => ({
      record_id: detail.id,
      company_id: detail.program?.company?.id || undefined,
      program_id: detail.program_id,
      dia: detail.dia,
      gauge: detail.gauge,
      rolls: detail.rolls,
      weight: detail.weight,
      machine_type: detail.machine_type,
    }));

    reset({
      employee_id: data.employee.id,
      shift_date: data.shift_date,
      shift_time: data.shift_time,
      employee_shift_details_attributes: transformedDetails,
    });
  };

  const fetchEmployees = async () => {
    const response = await authenticatedFetch(`${API_URL}/employees`);
    if (!response.ok) throw new Error("Failed to fetch employees");
    setEmployees(await response.json());
  };

  const fetchCompanies = async () => {
    const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);
    if (!response.ok) throw new Error("Failed to fetch companies");
    setCompanies(await response.json());
  };

  const fetchPrograms = async () => {
    const response = await authenticatedFetch(`${API_URL}/programs`);
    if (!response.ok) throw new Error("Failed to fetch programs");
    const data = await response.json();
    setPrograms(data.filter((p: Program) => p.status === "not_started" || p.status === "in_progress"));
  };

  const fetchMachines = async () => {
    const response = await authenticatedFetch(`${API_URL}/machines`);
    if (!response.ok) throw new Error("Failed to fetch machines");
    setMachines(await response.json());
  };

  const getFilteredPrograms = (companyId: number | undefined) => {
    if (!companyId) return [];
    return programs.filter((p) => p.company.id === companyId);
  };

  const getAvailableDias = (programId: number | undefined, selectedGauge?: string) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program?.program_details) return [];
    
    const filteredDetails = selectedGauge
      ? program.program_details.filter((d) => d.gauge === selectedGauge)
      : program.program_details;
    
    return Array.from(new Set(filteredDetails.map((d) => d.dia))).sort((a, b) => a - b);
  };

  const getAvailableGauges = (programId: number | undefined, selectedDia?: string) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program?.program_details) return [];
    
    const filteredDetails = selectedDia
      ? program.program_details.filter((d) => d.dia.toString() === selectedDia)
      : program.program_details;
    
    return Array.from(new Set(filteredDetails.map((d) => d.gauge))).sort();
  };

  const handleFormSubmit = async () => {
    const currentValues = getValues();
    const activeDetails = currentValues.employee_shift_details_attributes || [];
    
    if (activeDetails.length === 0) {
      setError("At least one detail is required");
      return;
    }
    
    setError("");
    
    if (!currentValues.employee_id) { setError("Employee is required"); return; }
    if (!currentValues.shift_date) { setError("Shift date is required"); return; }
    if (!currentValues.shift_time) { setError("Shift time is required"); return; }
    
    for (let i = 0; i < activeDetails.length; i++) {
      const detail = activeDetails[i];
      if (!detail.company_id) { setError(`Detail ${i + 1}: Company is required`); return; }
      if (!detail.program_id) { setError(`Detail ${i + 1}: Program is required`); return; }
      if (!detail.dia) { setError(`Detail ${i + 1}: Dia is required`); return; }
      if (!detail.gauge) { setError(`Detail ${i + 1}: Gauge is required`); return; }
      if (!detail.rolls || detail.rolls <= 0) { setError(`Detail ${i + 1}: Rolls must be positive`); return; }
      if (!detail.weight || detail.weight <= 0) { setError(`Detail ${i + 1}: Weight must be positive`); return; }
      if (!detail.machine_type) { setError(`Detail ${i + 1}: Machine type is required`); return; }
    }
    
    await onSubmit(currentValues);
  };

  const onSubmit = async (data: EmployeeShiftFormData) => {
    setSubmitting(true);
    setError("");

    try {
      // Process active details: convert record_id to id for backend
      const activeDetails = data.employee_shift_details_attributes.map(
        ({ company_id, record_id, _destroy, ...rest }) => {
          if (typeof record_id === 'number') {
            return { ...rest, id: record_id };
          }
          return rest;
        }
      );
      
      // Create deletion entries for soft delete
      const deletionEntries = deletedDetailIdsRef.current.map(deletedId => ({
        id: deletedId,
        _destroy: true
      }));
      
      const submitData = {
        ...data,
        employee_shift_details_attributes: [...activeDetails, ...deletionEntries],
      };

      const response = await authenticatedFetch(`${API_URL}/employee_shifts/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ employee_shift: submitData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.join(", ") || "Failed to update employee shift");
      }

      const responseData = await response.json();
      
      deletedDetailIdsRef.current = [];
      await fetchShift();
      setIsEditing(false);
      
      // Check if any programs transitioned to in_progress
      if (responseData.newly_started_programs && responseData.newly_started_programs.length > 0) {
        setNewlyStartedPrograms(responseData.newly_started_programs);
        setCurrentShareIndex(0);
        setShareDialogOpen(true);
      }
    } catch (err) {
      console.error("Error updating employee shift:", err);
      setError(err instanceof Error ? err.message : "Failed to update employee shift");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleShareDialogClose = () => {
    // Move to next program if there are more
    if (currentShareIndex < newlyStartedPrograms.length - 1) {
      setCurrentShareIndex(currentShareIndex + 1);
    } else {
      // All programs shown, close dialog
      setShareDialogOpen(false);
      setNewlyStartedPrograms([]);
      setCurrentShareIndex(0);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this shift?")) return;

    try {
      const response = await authenticatedFetch(`${API_URL}/employee_shifts/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Failed to delete shift");
      navigate("/employee-shifts");
    } catch (err) {
      console.error("Error deleting shift:", err);
      setError("Failed to delete shift");
    }
  };

  const addDetail = () => {
    append({
      company_id: undefined,
      program_id: undefined,
      dia: "",
      gauge: "",
      rolls: 0,
      weight: 0,
      machine_type: "rib",
    });
  };

  const removeDetail = (index: number) => {
    const detailValues = watch(`employee_shift_details_attributes.${index}`);
    const recordId = detailValues?.record_id;
    
    // If it's an existing record, track for deletion
    if (typeof recordId === 'number') {
      deletedDetailIdsRef.current = [...deletedDetailIdsRef.current, recordId];
    }
    
    remove(index);
    clearErrors(`employee_shift_details_attributes.${index}`);
  };

  const hasRelevantErrors = () => {
    if (!errors.employee_shift_details_attributes) return false;
    if (errors.employee_shift_details_attributes.message) return true;
    const detailErrors = errors.employee_shift_details_attributes as any;
    return Array.isArray(detailErrors) && detailErrors.some((error: any) => !!error);
  };

  const handleCancel = () => {
    deletedDetailIdsRef.current = [];
    fetchShift();
    setIsEditing(false);
  };

  const handleEdit = () => {
    deletedDetailIdsRef.current = [];
    setIsEditing(true);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          spacing={2} 
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <IconButton onClick={() => navigate("/employee-shifts")} color="primary" size={isMobile ? "small" : "medium"}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ flexGrow: 1 }}>
              {isEditing ? "Edit Employee Shift" : "Employee Shift Details"}
            </Typography>
          </Stack>
          <Stack 
            direction="row" 
            spacing={1} 
            sx={{ width: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 'auto' } }}
          >
            {!isEditing ? (
              <>
                <Button 
                  variant="contained" 
                  startIcon={!isMobile && <EditIcon />} 
                  onClick={handleEdit}
                  fullWidth={isMobile}
                  size={isMobile ? "small" : "medium"}
                >
                  Edit
                </Button>
                <Button 
                  variant="outlined" 
                  color="error" 
                  startIcon={!isMobile && <DeleteIcon />} 
                  onClick={handleDelete}
                  fullWidth={isMobile}
                  size={isMobile ? "small" : "medium"}
                >
                  Delete
                </Button>
              </>
            ) : (
              <Button 
                variant="outlined" 
                onClick={handleCancel}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {!isEditing && shift ? (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Shift Information</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
                    <Typography variant="body1">{shift.employee.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{shift.employee.phone}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Shift Date</Typography>
                    <Typography variant="body1">
                      {new Date(shift.shift_date).toLocaleDateString("en-US", {
                        year: "numeric", month: "long", day: "numeric",
                      })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Shift Time</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {shift.shift_time === "day" ? (
                        <WbSunnyIcon fontSize="small" color="warning" />
                      ) : (
                        <NightsStayIcon fontSize="small" color="info" />
                      )}
                      <Typography variant="body1">{shift.shift_time === "day" ? "Day" : "Night"}</Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Production Details</Typography>
                {isMobile ? (
                  <Stack spacing={2}>
                    {shift.employee_shift_details.map((detail: any, index: number) => {
                      const progressInfo = detail.progress_info;
                      const progressPercent = Number(progressInfo?.progress_percentage) || 0;
                      const completed = Number(progressInfo?.completed_weight) || 0;
                      const total = Number(progressInfo?.total_weight) || 0;
                      const remaining = total - completed;
                      
                      return (
                        <Paper key={detail.id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="caption" color="text.secondary">Company</Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (detail.program?.company?.id) {
                                      navigate(`/companies/${detail.program.company.id}`);
                                    }
                                  }}
                                  sx={{
                                    cursor: detail.program?.company?.id ? "pointer" : "default",
                                    color: detail.program?.company?.id ? "primary.main" : "inherit",
                                    textDecoration: detail.program?.company?.id ? "underline" : "none",
                                    "&:hover": { color: detail.program?.company?.id ? "primary.dark" : "inherit" },
                                  }}
                                >
                                  {detail.program?.company?.name || "-"}
                                </Typography>
                              </Box>
                              <Chip
                                label={detail.machine_type}
                                size="small"
                                sx={{
                                  bgcolor: detail.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                                  color: detail.machine_type === 'rib' ? '#000' : '#fff',
                                }}
                              />
                            </Stack>

                            <Box>
                              <Typography variant="caption" color="text.secondary">Program</Typography>
                              <Typography variant="body2">
                                <Link 
                                  to={`/programs/${detail.program_id}`}
                                  style={{ 
                                    color: '#1976d2', 
                                    textDecoration: 'none',
                                    fontWeight: 500
                                  }}
                                >
                                  {detail.program?.lot || `#${detail.program_id}`}
                                </Link>
                              </Typography>
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Dia</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <DiaCircle
                                    dia={detail.dia}
                                    gauge={detail.gauge}
                                    machineType={detail.machine_type}
                                    machines={machines}
                                    size="small"
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Gauge</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <GaugeChip gauge={detail.gauge} />
                                </Box>
                              </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Rolls</Typography>
                                <Typography variant="body2" fontWeight={500}>{detail.rolls}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Weight</Typography>
                                <Typography variant="body2" fontWeight={500}>{Number(detail.weight).toFixed(2)} kg</Typography>
                              </Grid>
                            </Grid>

                            <ProgressBar
                              type="weight"
                              done={completed}
                              total={total}
                              size="small"
                            />
                          </Stack>
                        </Paper>
                      );
                    })}

                    <Divider />

                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight="bold">Totals:</Typography>
                        <Stack direction="row" spacing={3}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Rolls</Typography>
                            <Typography variant="body2" fontWeight="bold">{formatInteger(shift.total_rolls || 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Weight</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {shift.total_weight ? Number(shift.total_weight).toFixed(2) : "0.00"} kg
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Company</TableCell>
                          <TableCell>Program</TableCell>
                          <TableCell>Dia</TableCell>
                          <TableCell>Gauge</TableCell>
                          <TableCell align="right">Rolls</TableCell>
                          <TableCell align="right">Weight (kg)</TableCell>
                          <TableCell>Machine Type</TableCell>
                          <TableCell>Progress</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shift.employee_shift_details.map((detail: any) => {
                          const progressInfo = detail.progress_info;
                          const progressPercent = Number(progressInfo?.progress_percentage) || 0;
                          const completed = Number(progressInfo?.completed_weight) || 0;
                          const total = Number(progressInfo?.total_weight) || 0;
                          const remaining = total - completed;
                          
                          return (
                            <TableRow key={detail.id}>
                              <TableCell>{detail.program?.company?.name || "-"}</TableCell>
                              <TableCell>
                                <Link 
                                  to={`/programs/${detail.program_id}`}
                                  style={{ 
                                    color: '#1976d2', 
                                    textDecoration: 'none',
                                    fontWeight: 500
                                  }}
                                >
                                  {detail.program?.lot || `#${detail.program_id}`}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <DiaCircle
                                  dia={detail.dia}
                                  gauge={detail.gauge}
                                  machineType={detail.machine_type}
                                  machines={machines}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell><GaugeChip gauge={detail.gauge} /></TableCell>
                              <TableCell align="right">{detail.rolls}</TableCell>
                              <TableCell align="right">{Number(detail.weight).toFixed(2)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={detail.machine_type}
                                  size="small"
                                  sx={{
                                    bgcolor: detail.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                                    color: detail.machine_type === 'rib' ? '#000' : '#fff',
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 200 }}>
                                <ProgressBar
                                  type="weight"
                                  done={completed}
                                  total={total}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={4} align="right">
                            <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold">{formatInteger(shift.total_rolls || 0)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold">
                              {shift.total_weight ? Number(shift.total_weight).toFixed(2) : "0.00"}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Shift Information</Typography>
                <Stack spacing={3}>
                  <Controller
                    name="employee_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.employee_id}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Employee *</Typography>
                        <Select {...field} value={field.value || ""}>
                          {employees.map((employee) => (
                            <MenuItem key={employee.id} value={employee.id}>
                              {employee.name} - {employee.phone}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.employee_id && <FormHelperText>{errors.employee_id.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="shift_date"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Shift Date"
                        type="date"
                        fullWidth
                        required
                        error={!!errors.shift_date}
                        helperText={errors.shift_date?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />

                  <Controller
                    name="shift_time"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.shift_time}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Shift Time *</Typography>
                        <Select {...field}>
                          <MenuItem value="day">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <WbSunnyIcon fontSize="small" />
                              <span>Day</span>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="night">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <NightsStayIcon fontSize="small" />
                              <span>Night</span>
                            </Stack>
                          </MenuItem>
                        </Select>
                        {errors.shift_time && <FormHelperText>{errors.shift_time.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  justifyContent="space-between" 
                  alignItems={{ xs: 'stretch', sm: 'center' }} 
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6">Production Details</Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    onClick={addDetail}
                    fullWidth={isMobile}
                    size={isMobile ? "small" : "medium"}
                  >
                    Add Detail
                  </Button>
                </Stack>

                {hasRelevantErrors() && fields.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.employee_shift_details_attributes?.message || "Please fix the errors in the details below"}
                  </Alert>
                )}

                {isMobile ? (
                  <Stack spacing={2}>
                    {fields.length === 0 ? (
                      <Paper variant="outlined" sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          No details added yet. Click "Add Detail" to start.
                        </Typography>
                      </Paper>
                    ) : (
                      fields.map((field, index) => {
                        const selectedCompanyId = watch(`employee_shift_details_attributes.${index}.company_id`);
                        const selectedProgramId = watch(`employee_shift_details_attributes.${index}.program_id`);
                        const selectedDia = watch(`employee_shift_details_attributes.${index}.dia`);
                        const selectedGauge = watch(`employee_shift_details_attributes.${index}.gauge`);
                        const filteredPrograms = getFilteredPrograms(selectedCompanyId);
                        const availableDias = getAvailableDias(selectedProgramId, selectedGauge);
                        const availableGauges = getAvailableGauges(selectedProgramId, selectedDia);

                        return (
                          <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={2}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight="bold">Detail #{index + 1}</Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeDetail(index)}
                                  disabled={fields.length === 1}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>

                              <Controller
                                name={`employee_shift_details_attributes.${index}.company_id`}
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.company_id}>
                                    <Typography variant="caption" color="text.secondary" gutterBottom>
                                      Company *
                                    </Typography>
                                    <Select {...field} value={field.value || ""} displayEmpty>
                                      <MenuItem value="" disabled>Select Company</MenuItem>
                                      {companies.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                                      ))}
                                    </Select>
                                    {errors.employee_shift_details_attributes?.[index]?.company_id && (
                                      <FormHelperText>{errors.employee_shift_details_attributes[index]?.company_id?.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />

                              <Controller
                                name={`employee_shift_details_attributes.${index}.program_id`}
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.program_id}>
                                    <Typography variant="caption" color="text.secondary" gutterBottom>
                                      Program *
                                    </Typography>
                                    <Select {...field} value={field.value || ""} displayEmpty disabled={!selectedCompanyId}>
                                      <MenuItem value="" disabled>
                                        {selectedCompanyId ? "Select Program" : "Select Company First"}
                                      </MenuItem>
                                      {filteredPrograms.map((program) => {
                                        const totalKg = program.program_details?.reduce((sum, d) => sum + Number(d.weight_in_kilo || 0), 0) || 0;
                                        const programText = `${program.lot || `#${program.id}`} (${totalKg.toFixed(2)} kg)`;
                                        return (
                                          <MenuItem key={program.id} value={program.id} title={programText}>
                                            {truncateText(programText, PROGRAM_NAME_MAX_WIDTH.select)}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                    {errors.employee_shift_details_attributes?.[index]?.program_id && (
                                      <FormHelperText>{errors.employee_shift_details_attributes[index]?.program_id?.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />

                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Controller
                                    name={`employee_shift_details_attributes.${index}.dia`}
                                    control={control}
                                    render={({ field }) => (
                                      <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.dia} disabled={!selectedProgramId}>
                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                          Dia *
                                        </Typography>
                                        <Select {...field} value={field.value || ""} displayEmpty>
                                          <MenuItem value="" disabled>
                                            {selectedProgramId ? "Select" : "Select Program"}
                                          </MenuItem>
                                          {availableDias.map((dia) => (
                                            <MenuItem key={dia} value={dia.toString()}>{dia}</MenuItem>
                                          ))}
                                        </Select>
                                        {errors.employee_shift_details_attributes?.[index]?.dia && (
                                          <FormHelperText>{errors.employee_shift_details_attributes[index]?.dia?.message}</FormHelperText>
                                        )}
                                      </FormControl>
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <Controller
                                    name={`employee_shift_details_attributes.${index}.gauge`}
                                    control={control}
                                    render={({ field }) => (
                                      <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.gauge} disabled={!selectedProgramId}>
                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                          Gauge *
                                        </Typography>
                                        <Select {...field} value={field.value || ""} displayEmpty>
                                          <MenuItem value="" disabled>
                                            {selectedProgramId ? "Select" : "Select Program"}
                                          </MenuItem>
                                          {availableGauges.map((gauge) => (
                                            <MenuItem key={gauge} value={gauge}>{gauge} GG</MenuItem>
                                          ))}
                                        </Select>
                                        {errors.employee_shift_details_attributes?.[index]?.gauge && (
                                          <FormHelperText>{errors.employee_shift_details_attributes[index]?.gauge?.message}</FormHelperText>
                                        )}
                                      </FormControl>
                                    )}
                                  />
                                </Grid>
                              </Grid>

                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Controller
                                    name={`employee_shift_details_attributes.${index}.rolls`}
                                    control={control}
                                    render={({ field }) => (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                          Rolls *
                                        </Typography>
                                        <TextField
                                          {...field}
                                          type="number"
                                          size="small"
                                          fullWidth
                                          error={!!errors.employee_shift_details_attributes?.[index]?.rolls}
                                          helperText={errors.employee_shift_details_attributes?.[index]?.rolls?.message}
                                        />
                                      </Box>
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <Controller
                                    name={`employee_shift_details_attributes.${index}.weight`}
                                    control={control}
                                    render={({ field }) => (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                          Weight (kg) *
                                        </Typography>
                                        <TextField
                                          {...field}
                                          type="number"
                                          size="small"
                                          fullWidth
                                          inputProps={{ step: "0.01" }}
                                          error={!!errors.employee_shift_details_attributes?.[index]?.weight}
                                          helperText={errors.employee_shift_details_attributes?.[index]?.weight?.message}
                                        />
                                      </Box>
                                    )}
                                  />
                                </Grid>
                              </Grid>

                              <Controller
                                name={`employee_shift_details_attributes.${index}.machine_type`}
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.machine_type} disabled={!selectedProgramId}>
                                    <Typography variant="caption" color="text.secondary" gutterBottom>
                                      Machine Type *
                                    </Typography>
                                    <Select {...field} value={field.value || ""} displayEmpty>
                                      <MenuItem value="" disabled>
                                        {selectedProgramId ? "Select Type" : "Select Program First"}
                                      </MenuItem>
                                      <MenuItem value="rib">Rib</MenuItem>
                                      <MenuItem value="fine">Fine</MenuItem>
                                    </Select>
                                  </FormControl>
                                )}
                              />
                            </Stack>
                          </Paper>
                        );
                      })
                    )}
                  </Stack>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 150 }}>Company *</TableCell>
                          <TableCell sx={{ minWidth: 180 }}>Program *</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Dia *</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Gauge *</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Rolls *</TableCell>
                          <TableCell sx={{ minWidth: 120 }}>Weight (kg) *</TableCell>
                          <TableCell sx={{ minWidth: 130 }}>Machine Type *</TableCell>
                          <TableCell align="center" sx={{ minWidth: 80 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                    <TableBody>
                      {fields.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No details added yet. Click "Add Detail" to start.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        fields.map((field, index) => {
                          const selectedCompanyId = watch(`employee_shift_details_attributes.${index}.company_id`);
                          const selectedProgramId = watch(`employee_shift_details_attributes.${index}.program_id`);
                          const selectedDia = watch(`employee_shift_details_attributes.${index}.dia`);
                          const selectedGauge = watch(`employee_shift_details_attributes.${index}.gauge`);
                          const filteredPrograms = getFilteredPrograms(selectedCompanyId);
                          const availableDias = getAvailableDias(selectedProgramId, selectedGauge);
                          const availableGauges = getAvailableGauges(selectedProgramId, selectedDia);

                          return (
                            <TableRow key={field.id}>
                              <TableCell sx={{ minWidth: 150 }}>
                                <Controller
                                  name={`employee_shift_details_attributes.${index}.company_id`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.company_id}>
                                      <Select {...field} value={field.value || ""} displayEmpty>
                                        <MenuItem value="" disabled>Select Company</MenuItem>
                                        {companies.map((company) => (
                                          <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                                        ))}
                                      </Select>
                                      {errors.employee_shift_details_attributes?.[index]?.company_id && (
                                        <FormHelperText>{errors.employee_shift_details_attributes[index]?.company_id?.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                )}
                              />
                            </TableCell>

                            <TableCell sx={{ minWidth: 180 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.program_id`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.program_id}>
                                      <Select {...field} value={field.value || ""} displayEmpty disabled={!selectedCompanyId}>
                                        <MenuItem value="" disabled>
                                          {selectedCompanyId ? "Select Program" : "Select Company First"}
                                        </MenuItem>
                                        {filteredPrograms.map((program) => {
                                          const totalKg = program.program_details?.reduce((sum, d) => sum + Number(d.weight_in_kilo || 0), 0) || 0;
                                          const programText = `${program.lot || `#${program.id}`} (${totalKg.toFixed(2)} kg)`;
                                          return (
                                            <MenuItem key={program.id} value={program.id} title={programText}>
                                              {truncateText(programText, PROGRAM_NAME_MAX_WIDTH.select)}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                      {errors.employee_shift_details_attributes?.[index]?.program_id && (
                                        <FormHelperText>{errors.employee_shift_details_attributes[index]?.program_id?.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                )}
                              />
                            </TableCell>

                            <TableCell sx={{ minWidth: 100 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.dia`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.dia} disabled={!selectedProgramId}>
                                      <Select {...field} value={field.value || ""} displayEmpty>
                                        <MenuItem value="" disabled>
                                          {selectedProgramId ? "Select Dia" : "Select Program First"}
                                        </MenuItem>
                                        {availableDias.map((dia) => (
                                          <MenuItem key={dia} value={dia.toString()}>{dia}</MenuItem>
                                        ))}
                                      </Select>
                                      {errors.employee_shift_details_attributes?.[index]?.dia && (
                                        <FormHelperText>{errors.employee_shift_details_attributes[index]?.dia?.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                )}
                              />
                            </TableCell>

                            <TableCell sx={{ minWidth: 100 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.gauge`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.gauge} disabled={!selectedProgramId}>
                                      <Select {...field} value={field.value || ""} displayEmpty>
                                        <MenuItem value="" disabled>
                                          {selectedProgramId ? "Select Gauge" : "Select Program First"}
                                        </MenuItem>
                                        {availableGauges.map((gauge) => (
                                          <MenuItem key={gauge} value={gauge}>{gauge} GG</MenuItem>
                                        ))}
                                      </Select>
                                      {errors.employee_shift_details_attributes?.[index]?.gauge && (
                                        <FormHelperText>{errors.employee_shift_details_attributes[index]?.gauge?.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                )}
                              />
                            </TableCell>

                            <TableCell sx={{ minWidth: 100 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.rolls`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      type="number"
                                      size="small"
                                      fullWidth
                                      error={!!errors.employee_shift_details_attributes?.[index]?.rolls}
                                      helperText={errors.employee_shift_details_attributes?.[index]?.rolls?.message}
                                    />
                                )}
                              />
                            </TableCell>

                            <TableCell sx={{ minWidth: 120 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.weight`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      type="number"
                                      size="small"
                                      fullWidth
                                      inputProps={{ step: "0.01" }}
                                      error={!!errors.employee_shift_details_attributes?.[index]?.weight}
                                      helperText={errors.employee_shift_details_attributes?.[index]?.weight?.message}
                                    />
                                )}
                              />
                            </TableCell>

                            <TableCell sx={{ minWidth: 130 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.machine_type`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.machine_type} disabled={!selectedProgramId}>
                                      <Select {...field} value={field.value || ""} displayEmpty>
                                        <MenuItem value="" disabled>
                                          {selectedProgramId ? "Select Type" : "Select Program First"}
                                        </MenuItem>
                                        <MenuItem value="rib">Rib</MenuItem>
                                        <MenuItem value="fine">Fine</MenuItem>
                                      </Select>
                                    </FormControl>
                                )}
                              />
                            </TableCell>

                            <TableCell align="center" sx={{ minWidth: 80 }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeDetail(index)}
                                disabled={fields.length === 1}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
              </CardContent>
            </Card>

            <Box sx={{ mt: 3 }}>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="flex-end"
              >
                <Button 
                  variant="outlined" 
                  onClick={handleCancel} 
                  disabled={submitting}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  variant="contained" 
                  disabled={submitting} 
                  onClick={handleFormSubmit}
                  fullWidth={isMobile}
                >
                  {submitting ? <CircularProgress size={24} /> : "Save Changes"}
                </Button>
              </Stack>
            </Box>
          </form>
        )}
      </Box>
      
      {/* Share Program Dialog for newly started programs */}
      {newlyStartedPrograms.length > 0 && newlyStartedPrograms[currentShareIndex] && (
        <ShareProgramDialog
          open={shareDialogOpen}
          onClose={handleShareDialogClose}
          shareToken={newlyStartedPrograms[currentShareIndex].share_token}
          companyName={newlyStartedPrograms[currentShareIndex].company_name}
          totalWeight={newlyStartedPrograms[currentShareIndex].total_weight}
          lot={newlyStartedPrograms[currentShareIndex].lot}
        />
      )}
    </Container>
  );
}
